import React, { Component } from 'react';
import { connect } from "react-redux";
import PropTypes from "prop-types";
import isEmpty from "is-empty";
import axios from "axios";
import { Store } from 'react-notifications-component';
import { User, Mail, FileText, MessageSquare } from 'lucide-react';
import { trackEvent, AnalyticsEvents } from '../../utils/analytics';

class ContactForm extends Component {

	constructor(props) {
		super(props);

		this.MESSAGE_LIMIT = 1500;
		this.RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

		this.state = {
			name: '',
			email: '',
			subject: '',
			message: '',
			submitting: false,
			recaptchaLoaded: false
		};

		this.optionsNotification = {
			title: "Message Notification",
			message: "",
			type: "success",
			insert: "top",
			container: "top-right",
			animationIn: ["animated", "fadeIn"],
			animationOut: ["animated", "fadeOut"],
			dismiss: {
				duration: 10 * 1000,
				onScreen: true
			}
		};

		this.handleChange = this.handleChange.bind(this);
		this.onSubmit = this.onSubmit.bind(this);
		this.responseSend = this.responseSend.bind(this);
		this.loadRecaptcha = this.loadRecaptcha.bind(this);
		this.executeRecaptcha = this.executeRecaptcha.bind(this);
		this.autoFillForm = this.autoFillForm.bind(this);
		this.mapRef = React.createRef();
		this.map = null;
		this.marker = null;
	}

	componentDidMount() {
		// Charger reCAPTCHA v3 si la clé est disponible
		if (this.RECAPTCHA_SITE_KEY) {
			this.loadRecaptcha();
		}

		// Initialiser la carte Leaflet
		this.initMap();
	}

	componentWillUnmount() {
		// Nettoyer la carte lors du démontage du composant
		if (this.map) {
			this.map.remove();
			this.map = null;
			this.marker = null;
		}
	}

	initMap() {
		// Attendre que Leaflet soit chargé
		if (typeof window.L === 'undefined') {
			setTimeout(() => this.initMap(), 100);
			return;
		}

		const mapElement = document.getElementById('map');
		if (!mapElement || this.map) {
			return;
		}

		try {
			// Coordonnées de Brest, France
			const brestLat = 48.3904;
			const brestLng = -4.4861;

			// Initialiser la carte
			this.map = window.L.map('map').setView([brestLat, brestLng], 13);

			// Ajouter les tuiles OpenStreetMap
			window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
				attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
				maxZoom: 19
			}).addTo(this.map);

			// Ajouter un marqueur
			this.marker = window.L.marker([brestLat, brestLng]).addTo(this.map);
			this.marker.bindPopup('<b>Brest, France</b>').openPopup();
		} catch (error) {
			console.error('Erreur lors de l\'initialisation de la carte Leaflet:', error);
		}
	}

	loadRecaptcha() {
		if (!this.RECAPTCHA_SITE_KEY) {
			console.warn('[ContactForm] reCAPTCHA site key not configured. Set REACT_APP_RECAPTCHA_SITE_KEY in .env');
			return;
		}

		// Vérifier si reCAPTCHA est déjà chargé
		if (window.grecaptcha && window.grecaptcha.ready) {
			console.log('[ContactForm] reCAPTCHA already loaded');
			window.grecaptcha.ready(() => {
				this.setState({ recaptchaLoaded: true });
			});
			return;
		}

		// Vérifier si le script est déjà en cours de chargement
		if (document.querySelector(`script[src*="recaptcha/api.js"]`)) {
			console.log('[ContactForm] reCAPTCHA script already in DOM, waiting for load...');
			// Attendre que le script se charge
			const checkInterval = setInterval(() => {
				if (window.grecaptcha && window.grecaptcha.ready) {
					clearInterval(checkInterval);
					window.grecaptcha.ready(() => {
						this.setState({ recaptchaLoaded: true });
					});
				}
			}, 100);

			// Timeout après 10 secondes
			setTimeout(() => {
				clearInterval(checkInterval);
				if (!window.grecaptcha) {
					console.error('[ContactForm] reCAPTCHA script failed to load after 10 seconds');
				}
			}, 10000);
			return;
		}

		// Charger le script reCAPTCHA
		console.log('[ContactForm] Loading reCAPTCHA script...');
		const script = document.createElement('script');
		script.src = `https://www.google.com/recaptcha/api.js?render=${this.RECAPTCHA_SITE_KEY}`;
		script.async = true;
		script.defer = true;
		script.onload = () => {
			console.log('[ContactForm] reCAPTCHA script loaded');
			if (window.grecaptcha && window.grecaptcha.ready) {
				window.grecaptcha.ready(() => {
					console.log('[ContactForm] reCAPTCHA ready');
					this.setState({ recaptchaLoaded: true });
				});
			} else {
				console.error('[ContactForm] reCAPTCHA script loaded but window.grecaptcha is not available');
			}
		};
		script.onerror = () => {
			console.error('[ContactForm] Failed to load reCAPTCHA script. Check your internet connection and ad blockers.');
		};
		document.head.appendChild(script);
	}

	async executeRecaptcha() {
		if (!this.RECAPTCHA_SITE_KEY) {
			console.warn('[ContactForm] reCAPTCHA site key not configured');
			return null;
		}

		if (!window.grecaptcha) {
			console.error('[ContactForm] reCAPTCHA script not loaded. window.grecaptcha is undefined');
			return null;
		}

		if (!window.grecaptcha.ready) {
			console.error('[ContactForm] reCAPTCHA not ready');
			return null;
		}

		try {
			const token = await window.grecaptcha.execute(this.RECAPTCHA_SITE_KEY, { action: 'submit' });
			console.log('[ContactForm] reCAPTCHA token generated successfully');
			return token;
		} catch (error) {
			console.error('[ContactForm] reCAPTCHA execution error:', error);
			return null;
		}
	}

	async onSubmit(e) {
		e.preventDefault();
		e.stopPropagation();

		if (this.state.submitting) {
			return;
		}

		let { name, email, subject, message } = this.state;

		if (isEmpty(name)) {
			return Store.addNotification({
				...this.optionsNotification,
				title: 'Donnée manquant !',
				type: 'danger',
				message: 'Attention ! Le nom est manquant...'
			});
		} else if (isEmpty(email)) {
			return Store.addNotification({
				...this.optionsNotification,
				title: 'Donnée manquant !',
				type: 'danger',
				message: 'Attention ! L\'email est manquant...'
			});
		} else if (isEmpty(subject)) {
			return Store.addNotification({
				...this.optionsNotification,
				title: 'Donnée manquant !',
				type: 'danger',
				message: 'Attention ! Le sujet est manquant...'
			});
		} else if (isEmpty(message)) {
			return Store.addNotification({
				...this.optionsNotification,
				title: 'Donnée manquant !',
				type: 'danger',
				message: 'Attention ! Le message est vide...'
			});
		}

		this.setState({ submitting: true });

		try {
			// Exécuter reCAPTCHA v3 si la clé est configurée
			let recaptchaToken = null;
			if (this.RECAPTCHA_SITE_KEY) {
				console.log('[ContactForm] Executing reCAPTCHA...');
				console.log('[ContactForm] reCAPTCHA state:', {
					siteKey: this.RECAPTCHA_SITE_KEY ? 'configured' : 'missing',
					grecaptcha: !!window.grecaptcha,
					recaptchaLoaded: this.state.recaptchaLoaded
				});

				// Attendre que reCAPTCHA soit chargé si nécessaire
				if (!window.grecaptcha && !this.state.recaptchaLoaded) {
					console.warn('[ContactForm] reCAPTCHA not loaded yet, waiting...');
					// Attendre jusqu'à 5 secondes que reCAPTCHA se charge
					for (let i = 0; i < 50; i++) {
						await new Promise(resolve => setTimeout(resolve, 100));
						if (window.grecaptcha) {
							console.log('[ContactForm] reCAPTCHA loaded after wait');
							break;
						}
					}
				}

				recaptchaToken = await this.executeRecaptcha();

				// Si reCAPTCHA est requis mais n'a pas pu être exécuté
				if (!recaptchaToken) {
					console.error('[ContactForm] reCAPTCHA token is null. Details:', {
						grecaptcha: !!window.grecaptcha,
						recaptchaLoaded: this.state.recaptchaLoaded,
						siteKey: this.RECAPTCHA_SITE_KEY ? 'configured' : 'missing'
					});

					// Vérifier si c'est un problème de chargement
					if (!window.grecaptcha) {
						this.setState({ submitting: false });
						return Store.addNotification({
							...this.optionsNotification,
							title: 'Erreur de sécurité',
							type: 'danger',
							message: 'Impossible de charger le système de vérification. Veuillez désactiver votre bloqueur de publicités et réessayer.'
						});
					}

					// Sinon, c'est une erreur d'exécution
					this.setState({ submitting: false });
					return Store.addNotification({
						...this.optionsNotification,
						title: 'Erreur de sécurité',
						type: 'danger',
						message: 'Erreur lors de la vérification de sécurité. Veuillez réessayer.'
					});
				}

				console.log('[ContactForm] reCAPTCHA token obtained successfully');
			} else {
				console.warn('[ContactForm] reCAPTCHA site key not configured, skipping verification');
			}

			// Envoyer le formulaire
			const response = await axios.post('/api/contacts', {
				name,
				email,
				subject,
				message,
				recaptchaToken
			});

			if (response.data.error) {
				Store.addNotification({
					...this.optionsNotification,
					title: 'Erreur',
					type: 'danger',
					message: response.data.message || 'Une erreur est survenue'
				});
			} else {
				Store.addNotification({
					...this.optionsNotification,
					title: 'Message envoyé',
					type: 'success',
					message: response.data.message || 'Votre message a bien été envoyé !'
				});

				trackEvent(AnalyticsEvents.CONTACT_SUBMIT);

				// Réinitialiser le formulaire
				this.setState({
					name: '',
					email: '',
					subject: '',
					message: ''
				});
			}
		} catch (error) {
			console.error('Contact form error:', error);
			const errorMessage = error.response?.data?.message || error.message || 'Une erreur est survenue lors de l\'envoi de votre message.';
			Store.addNotification({
				...this.optionsNotification,
				title: 'Erreur',
				type: 'danger',
				message: errorMessage
			});
		} finally {
			this.setState({ submitting: false });
		}
	}

	responseSend(error, data) {
		if (error) {
			return Store.addNotification({
				...this.optionsNotification,
				title: 'Donnée casser',
				type: 'danger',
				message: data.message
			});
		}

		return Store.addNotification({
			...this.optionsNotification,
			title: 'Message envoyée',
			type: 'succes',
			message: data.message
		});
	}

	handleChange(e) {
		if (e.target.id) {
			if (e.target.id.includes("pfContact__name")) {
				this.setState({
					name: e.target.value
				});
			} else if (e.target.id.includes("pfContact__email")) {
				this.setState({
					email: e.target.value
				});
			} else if (e.target.id.includes("pfContact__subject")) {
				this.setState({
					subject: e.target.value
				});
			} else if (e.target.id.includes("pfContact__message")) {
				if (e.target.value.length <= this.MESSAGE_LIMIT) {
					this.setState({
						message: e.target.value
					});
				}
			}
		}
	}

	autoFillForm() {
		// Générer des données aléatoires pour le test
		const firstNames = ['Jean', 'Marie', 'Pierre', 'Sophie', 'Thomas', 'Emma', 'Lucas', 'Léa', 'Antoine', 'Camille'];
		const lastNames = ['Dupont', 'Martin', 'Bernard', 'Dubois', 'Laurent', 'Moreau', 'Simon', 'Michel', 'Garcia', 'David'];
		const domains = ['gmail.com', 'yahoo.fr', 'outlook.com', 'hotmail.com', 'example.com'];
		const subjects = [
			'Demande de renseignements',
			'Proposition de collaboration',
			'Question technique',
			'Demande de devis',
			'Feedback sur le portfolio',
			'Opportunité professionnelle'
		];
		const messageTemplates = [
			'Bonjour,\n\nJe souhaiterais obtenir plus d\'informations concernant vos services. Pourriez-vous me recontacter à votre convenance ?\n\nCordialement.',
			'Bonjour,\n\nJe vous contacte pour discuter d\'une éventuelle collaboration sur un projet intéressant. Seriez-vous disponible pour en discuter ?\n\nMerci d\'avance.',
			'Bonjour,\n\nJ\'ai une question technique concernant votre expertise. Pourriez-vous m\'éclairer sur ce point ?\n\nMerci pour votre temps.',
			'Bonjour,\n\nJe serais intéressé(e) par vos services. Pourriez-vous me faire parvenir un devis détaillé ?\n\nCordialement.',
			'Bonjour,\n\nJ\'ai visité votre portfolio et je suis impressionné(e) par votre travail. J\'aimerais en savoir plus sur vos projets.\n\nBien à vous.'
		];

		const randomFirstName = firstNames[Math.floor(Math.random() * firstNames.length)];
		const randomLastName = lastNames[Math.floor(Math.random() * lastNames.length)];
		const randomDomain = domains[Math.floor(Math.random() * domains.length)];
		const randomSubject = subjects[Math.floor(Math.random() * subjects.length)];
		const randomMessage = messageTemplates[Math.floor(Math.random() * messageTemplates.length)];

		this.setState({
			name: `${randomFirstName} ${randomLastName}`,
			email: `${randomFirstName.toLowerCase()}.${randomLastName.toLowerCase()}@${randomDomain}`,
			subject: randomSubject,
			message: randomMessage
		});
	}

	render() {
		let { _preferences: preferences } = this.props;

		return (
			<>
				{/* Start contact-page Area */}
				<section className="contact-page-area section-gap-top">
					<div className="container">
						<div className="row">
							<div className="col-lg-4 d-flex flex-column address-wrap">
								<div className="single-contact-address d-flex flex-row">
									<div className="icon">
										<span className="lnr lnr-home"> </span>
									</div>
									<div className="contact-details">
										<h5>{preferences._address.state}, {preferences._address.country}</h5>
										<p>
											{preferences._address.address}
										</p>
									</div>
								</div>
								<div className="single-contact-address d-flex flex-row">
									<div className="icon">
										<span className="lnr lnr-phone-handset"> </span>
									</div>
									<div className="contact-details">
										<h5>{preferences._tel}</h5>
										<p>À tous moment</p>
									</div>
								</div>
								<div className="single-contact-address d-flex flex-row">
									<div className="icon">
										<span className="lnr lnr-envelope"> </span>
									</div>
									<div className="contact-details">
										<h5>{preferences._email}</h5>
										<p>Envoyez-nous votre demande à tout moment !</p>
									</div>
								</div>
							</div>
							<div className="col-lg-8">
								<form className="form-area contact-form" id="myForm" action="mail.php" method="post">
									<div className="row">
										<div className="col-lg-6 form-group form-group-top">
											<label htmlFor="pfContact__name" className="contact-label">
												<User size={18} className="contact-icon" />
												<span>Nom</span>
											</label>
											<input name="name" id="pfContact__name"
												placeholder="Entrer votre nom"
												onChange={this.handleChange}
												className="contact-input"
												required
												value={this.state.name}
												type="text" />

											<label htmlFor="pfContact__email" className="contact-label">
												<Mail size={18} className="contact-icon" />
												<span>Email</span>
											</label>
											<input name="email" id="pfContact__email"
												placeholder="Entrer votre email"







												onChange={this.handleChange}
												className="contact-input"
												required
												value={this.state.email}
												type="email" />

											<label htmlFor="pfContact__subject" className="contact-label">
												<FileText size={18} className="contact-icon" />
												<span>Sujet</span>
											</label>
											<input name="subject" id="pfContact__subject"
												placeholder="Entrer le sujet"
												onChange={this.handleChange}
												className="contact-input"
												required
												value={this.state.subject}
												type="text" />
										</div>
										<div className="col-lg-6 form-group">
											<label htmlFor="pfContact__message" className="contact-label">
												<MessageSquare size={18} className="contact-icon" />
												<span>Message</span>
											</label>
											<textarea
												className="contact-textarea"
												name="message"
												id="pfContact__message"
												placeholder="Votre message..."
												onChange={this.handleChange}
												required
												value={this.state.message} >
											</textarea>
											<div className="d-flex justify-content-end">
												<small>{this.state.message.length} / {this.MESSAGE_LIMIT}</small>
											</div>
										</div>
										<div className="col-lg-12">
											<div className="alert-msg" style={{ textAlign: 'left' }}> </div>

											<div style={{ float: 'right', display: 'flex', gap: '10px' }}>
												{process.env.NODE_ENV === 'development' && (
													<button
														type="button"
														className="contact-submit-btn"
														style={{ 
															backgroundColor: '#6c757d',
															borderColor: '#6c757d',
															opacity: this.state.submitting ? 0.6 : 1,
															cursor: this.state.submitting ? 'not-allowed' : 'pointer'
														}}
														onClick={this.autoFillForm}
														disabled={this.state.submitting}
													>
														Auto remplir
													</button>
												)}
											<button
												className="contact-submit-btn"
												data-text={this.state.submitting ? "Envoi..." : "Envoyer"}
												onClick={this.onSubmit}
												disabled={this.state.submitting}
											>
												{(this.state.submitting ? "Envoi..." : "Envoyer").split("").map((rows, keys) => <span key={keys}>{rows}</span>)}
											</button>
											</div>
										</div>
									</div>
								</form>
							</div>

							<div className="col-lg-12">
								<div className="map-wrap" style={{ width: '100%', height: '445px' }} id="map" ref={this.mapRef}> </div>
							</div>
						</div>
					</div>
				</section>
				{/* End contact-page Area */}
			</>
		);
	}
}

ContactForm.protoType = {
	_preferences: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
	_preferences: state._preferences
});

export default connect(
	mapStateToProps,
	{}
)(ContactForm);